import React, { useEffect, useState, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Grid } from "@material-ui/core";
import moment from "moment";

import LoadingIndicator from "../../../../Component/LoadingIndicator";
import NoAccessScreen from "../../../../Component/ErrorMessage/NoAccess";
import CheckSummary from "./components/CheckSummary";
import CheckTimeline from "./components/CheckTimeline";
import ChecksByCountry from "./components/ChecksByCountry";
import ChecksBySource from "./components/ChecksBySource";
import LatestChecks from "./components/LatestChecks";
import RejectedAndCancelledChecks from "./components/RejectedAndCancelledChecks";
import TopChecksByDuration from "./components/TopChecksByDuration";
import CheckAvgTimeByStatus from "./components/CheckAvgTimeByStatus";
import FilterPanel from "./components/FiltersPanel";

import { getDashboard } from "../../../../API/AdminDashboard";
import { getListValues } from "../../../../API/List";

import ModalDialogContext from "../../../../Context/ModalDialog/ModalDialogContext";
import { recordTypes } from "../../../../API/config";
import styles from "./styles";

const useStyles = makeStyles(styles);

const defaultFilters = {
  country: { value: null, label: "ALL" },
  timeRange: 6,
  toDate: moment(),
  fromDate: moment().subtract(6, "months"),
};

const DashboardContainer = (props) => {
  const { hasPermission } = props;

  const classes = useStyles();

  const [dashboardData, setDashboardData] = useState(null);
  const [countryList, setCountryList] = useState([]);
  const [hasDashboardPermission, setHasDashboardPermission] = useState();
  const [loading, setLoading] = useState(false);

  const { showErrorMessage } = useContext(ModalDialogContext);

  const [filters, setFilters] = useState(defaultFilters);

  useEffect(() => {
    const loadCountryList = async () => {
      try {
        setLoading(true);
        const results = await getListValues({ listType: "CL" });
        const { data = [] } = results;
        data.unshift({
          id: null,
          name: "All",
        });
        setCountryList(data.map((val) => ({ value: val.code, label: val.value })));
      } catch (error) {
        showErrorMessage({ error });
      }
      setLoading(false);
    };

    const evalPermissions = async () => {
      const hasDashboardPermission = await hasPermission(
        recordTypes.adminDashboard.name,
        recordTypes.adminDashboard.actions.view.name
      );

      setHasDashboardPermission(hasDashboardPermission);
    };

    loadCountryList();
    evalPermissions();
  }, []);

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        const parameters = {
          dateFrom: filters.fromDate,
          dateTo: filters.toDate,
          countryId: filters.country.value,
        };
        const result = await getDashboard(parameters);
        const { data } = result;
        setDashboardData(data);
      } catch (error) {
        showErrorMessage({ error });
      }
      setLoading(false);
    };
    if (filters) {
      loadDashboardData();
    }
  }, [filters]);

  const onFilterChange = (filter) => {
    setFilters(filter);
  };

  const onResetFilter = () => {
    setFilters(defaultFilters);
  };

  if (loading)
    return (
      <div
        style={{
          height: 300,
          width: "100%",
          marginLeft: 110,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <LoadingIndicator />
      </div>
    );

  if (!loading && !hasDashboardPermission) {
    return <NoAccessScreen />;
  }

  if (!loading && dashboardData == null) return null;

  return (
    <Grid container spacing={2} className={classes.root}>
      <Grid item xs={12}>
        <FilterPanel
          classes={classes}
          countryList={countryList}
          onFilterChange={onFilterChange}
          onResetFilter={onResetFilter}
          filters={filters}
        />
      </Grid>

      <Grid item xs={12}>
        <CheckSummary
          checksByStatus={dashboardData.checksByStatus}
          recordsByType={dashboardData.recordsByType}
          checksByScore={dashboardData.checksByScore}
          classes={classes}
        />
      </Grid>

      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={8}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <CheckTimeline
                  classes={classes}
                  data={dashboardData.checkTimeline}
                />
              </Grid>
              <Grid item xs={12}>
                <ChecksByCountry
                  classes={classes}
                  data={dashboardData.checksByCountry}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={4}>
            <LatestChecks
              classes={classes}
              data={dashboardData.latestChecks}
            />
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <Grid container spacing={2}>
          <Grid item xs={8}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <CheckAvgTimeByStatus classes={classes} data={dashboardData.checkAvgTimeByStatus} />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={4}>
            <TopChecksByDuration
              classes={classes}
              data={dashboardData.topChecksByTime}
            />
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={6}>
        <ChecksBySource classes={classes} data={dashboardData.checksBySource} />
      </Grid>
      <Grid item xs={6}>
        <RejectedAndCancelledChecks
          classes={classes}
          data={dashboardData.checksByStatus}
        />
      </Grid>
    </Grid>
  );
};

export default DashboardContainer;
